import { axios } from '@complispace/cs-design-system';
import { ROLES, VIEW_TYPES } from '@/constants';
import { content, client as apiClient } from '@/api';
import * as types from '../../mutationTypes';
import sessionManager from '../session/sessionManager';

// Roles come from Quicklinks
const qlBaseURL = process.env.QUICK_LINKS_API_URL;
const qlRoleBaseUrl = `${qlBaseURL}/v1/auth/role`;

// FYI, using Javascript proxy so returns 0 if property not in roleMap
const defaultZeroHandler = {
  get(obj, prop) {
    return prop in obj ? obj[prop] : 0;
  }
};
const roleMap = new Proxy(
  {
    'CompliSpace Administrator': 4,
    'Group Administrator': 3,
    'Client Administrator': 2,
    'Client User': 1
  },
  defaultZeroHandler
);

// helper functions
// TODO: should we move this outside so we can unit test this?
const getViewType = (state) => {
  if (state.role === ROLES.CLIENT_USER) {
    return VIEW_TYPES.CLIENT;
  }

  if (state.role > ROLES.CLIENT_USER && state.draftMode) {
    return VIEW_TYPES.ADMIN_DRAFT;
  }
  return VIEW_TYPES.ADMIN_PUBLISH;
};

export default {
  async getSiteType({ commit, rootState: { client }, dispatch }) {
    await dispatch('client/get', null, { root: true });
    const { children, parentId } = client;

    if (parentId !== null) {
      commit(types.SET_SITE_TYPE, 2); // Child School Site in Group
    } else if (children && children.length > 0) {
      commit(types.SET_SITE_TYPE, 3); // Enterprise Group Site
    } else {
      commit(types.SET_SITE_TYPE, 1); // Single School Site
    }
  },

  async isPagePublic({ commit, dispatch, rootState }, details = {}) {
    // This method is invoked when authorisation/isLoggedIn false to check if the page is public
    // Clearing any tokens. There could be expired tokens in the session
    sessionManager.clear();

    // Set the x-site-uri header for CE api calls
    sessionManager.setAdditionalHeaders();

    dispatch('data/dataManagerSetup', { userID: rootState.client.id }, { root: true });

    let isPagePublic = false;

    const result = await content.getPageDetails(details.moduleId, details.pagePath);
    if (result.status === 200 && result.data.public) {
      const { logoUri, displayName } = result.data;
      isPagePublic = true;
      const client = JSON.parse(JSON.stringify(rootState.client));
      client.logoUri = logoUri;
      client.displayName = displayName;
      commit(types.CLIENT_UPDATE_CLIENT, client, { root: true });
    }

    return isPagePublic;
  },

  saveUserRole({ commit, state }, value) {
    let userRole;
    if (value !== undefined) {
      userRole = value;
    } else {
      userRole = parseInt(sessionManager.getUserRoleFromStorage(), 10);
    }

    sessionManager.saveUserRole(userRole);
    commit(types.SET_ROLE, userRole);

    const currentViewType = getViewType(state);

    commit(types.SET_VIEW_TYPE, currentViewType);

    // TODO: Move this to App.vue switchRole() function.
    // Reset pageCommitOID and pageCommitTimestamp, which are used to indicate that we are viewing
    // a previous version of content corresponding to the commit OID on the main page renderer.
    // Client view still allows read-only Page History / Compare via the Tools Panel; this only
    // clears the main-page historical commit so the live page shows current content.
    if (currentViewType === VIEW_TYPES.CLIENT) {
      commit(types.DATA_SET_PAGE_COMMIT_OID, '', { root: true });
      commit(types.DATA_SET_PAGE_COMMIT_TIMESTAMP, '', { root: true });
    }
  },

  // TODO: rename to saveOriginalRole
  saveOriginalAdminRole({ commit }, value) {
    let originalAdminRole;
    if (value !== undefined) {
      originalAdminRole = value;
    } else {
      originalAdminRole = parseInt(sessionManager.getOriginalAdminRoleFromStorage(), 10);
    }

    sessionManager.saveOriginalAdminRole(originalAdminRole);
    commit(types.SET_ORIGINAL_ADMIN_ROLE, originalAdminRole);
  },

  async saveDraftMode({ commit, state }, value) {
    let draftMode;

    if (value !== undefined) {
      draftMode = value;
    } else {
      draftMode = sessionManager.getDraftModeFromStorage() === 'true';
    }

    sessionManager.saveDraftMode(draftMode);
    commit(types.SET_DRAFT_MODE, draftMode);

    const currentViewType = getViewType(state);
    commit(types.SET_VIEW_TYPE, currentViewType);

    // Reset pageCommitOID and pageCommitTimestamp, which are used to indicate that we are viewing a previous version of content corresponding to the commit OID.
    // When we are in draft mode, we only see the current content version with current context key values.
    if (draftMode || currentViewType === VIEW_TYPES.ADMIN_DRAFT) {
      commit(types.DATA_SET_PAGE_COMMIT_OID, '', { root: true });
      commit(types.DATA_SET_PAGE_COMMIT_TIMESTAMP, '', { root: true });
    }
  },

  async getUserRole() {
    let response;
    try {
      response = await axios.get(`${qlRoleBaseUrl}?organisation=${sessionManager.getSubdomain()}`);
      return roleMap[response.data];
    } catch (e) {
      console.log(e);
      throw new Error('Invalid user role returned. Check user rights.');
    }
  },

  async logout({ rootGetters, dispatch }) {
    await apiClient.logoutAha();

    const isHubEnabled = rootGetters['hub/isEnabled'];
    const isAuth0Enabled = rootGetters['auth0/enabled'];

    // Check Hub first (uses hub-session)
    if (isHubEnabled) {
      await dispatch('hub-session/logout', {}, { root: true });
    } else if (isAuth0Enabled) {
      // Auth0 uses cs-session
      await dispatch('cs-session/logout', false, { root: true });
    } else {
      // Legacy IDP logout
      await dispatch('session/logout', undefined, { root: true });
    }
    await dispatch(types.SESSION_SET_IS_LOGIN_BANNER_SHOWN, false, { root: true });
  }
};
